import React, { createContext, useState, useEffect } from 'react';
import { login as loginAPI, logout as logoutAPI, getMe, getCurrentUser, getTokenFromStorage } from '../services/authService';

// Auth Context
export const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(getTokenFromStorage());

  
  
  useEffect(() => {
    // console.log("AuthContext - initAuth running");
    const initAuth = async () => {
      // Yükleme durumunu başlat
      setIsLoading(true);
      
      try {
        // LocalStorage'dan mevcut kullanıcı bilgisini ve token kontrolü
        const storedUser = getCurrentUser();
        const storedToken = getTokenFromStorage();
        const hasToken = storedToken !== null;
        
        // console.log("AuthContext - initAuth:", { hasToken, hasUser: !!storedUser });
        
        // Token değerini güncelle
        setToken(storedToken);
        
        if (hasToken && storedUser) {
          try {
            // Kullanıcı bilgilerini sunucudan yenile
            const userData = await getMe();
            
            // Kullanıcı verileri başarıyla alındıysa, state'i güncelle
            setUser(userData);
            setIsAuthenticated(true);
            // console.log("AuthContext - User authenticated with fresh data");
          } catch (err) {
            // console.warn('API error details:', err.response?.data || err.message);
            // API hatası durumunda, localStorage'daki kullanıcı bilgilerini kullan
            // console.warn('Failed to refresh user data from API, using stored data');
            
            // Yine de token varsa kullanıcıyı oturum açmış olarak kabul et
            // Bu, geçici API sorunlarında kullanıcı deneyimini korur
            if (hasToken) {
              setUser(storedUser);
              setIsAuthenticated(true);
              // console.log("AuthContext - User authenticated with stored data");
            } else {
              // Token yoksa oturumu temizle
              setUser(null);
              setToken(null);
              setIsAuthenticated(false);
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              // console.log("AuthContext - Auth cleared due to token missing");
            }
          }
        } else {
          // Token veya kullanıcı bilgisi yoksa oturumu temizle
          // console.log("Missing token or user data, not authenticated");
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
          // console.log("AuthContext - Not authenticated at init");
        }
      } catch (err) {
        // console.error('Auth initialization error:', err);
        setError('Kimlik doğrulama sırasında bir hata oluştu.');
      } finally {
        // Yükleme durumunu güncelle - burada referans sorunu yok
        setTimeout(() => {
          setIsLoading(false);
          // console.log("AuthContext - Init completed, isLoading set to false");
        }, 100);
      }
    };

    initAuth();
  }, []);

  // Token değişikliklerini izle
  useEffect(() => {
    // console.log("AuthContext - token changed:", token ? "exists" : "null");
  }, [token]);

  const login = async (username, password) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await loginAPI(username, password);
      
      if (response.success) {
        setUser(response.user);
        setToken(response.token);
        setIsAuthenticated(true);
        // console.log("AuthContext - Login successful, token set");
        
        // Yükleme durumunu biraz sonra güncelle - state değişikliklerinin uygulanması için zaman ver
        setTimeout(() => {
          setIsLoading(false);
        }, 100);
        
        return { success: true, user: response.user };
      } else {
        setError(response.message || 'Giriş başarısız');
        setIsLoading(false);
        return { success: false, message: response.message };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Giriş sırasında bir hata oluştu';
      setError(errorMessage);
      setIsLoading(false);
      return { success: false, message: errorMessage };
    }
  };

  const logout = async () => {
    setIsLoading(true);

    try {
      await logoutAPI();
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      // console.log("AuthContext - Logout successful, token cleared");
      return { success: true };
    } catch (err) {
      // console.error('Logout error:', err);
      // Hata durumunda bile state'i temizle
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      // console.log("AuthContext - Logout with error, token still cleared");
      return { success: false, message: 'Çıkış sırasında bir hata oluştu' };
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        error,
        token,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 